import React, { useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Badge } from 'primereact/badge';
import { ProgressBar } from 'primereact/progressbar';
import { Tag } from 'primereact/tag';
import { useClientJobs } from '../../hooks/useClientJobs';
import { BackgroundJob, JobStatus } from '../../types';
import { JobDetailsDialog, formatJobType } from './JobDetailsDialog';

export interface JobIndicatorProps {
  clientId: string | null | undefined;
}

const statusSeverityMap: Record<JobStatus, 'success' | 'info' | 'warning' | 'danger'> = {
  pending: 'warning',
  running: 'info',
  completed: 'success',
  failed: 'danger',
};

const getProgressPercent = (job: BackgroundJob): number => {
  if (job.progress_total <= 0) return 0;
  return Math.round((job.progress_current / job.progress_total) * 100);
};

export const JobIndicator: React.FC<JobIndicatorProps> = ({ clientId }) => {
  const overlayRef = useRef<OverlayPanel>(null);
  const [selectedJob, setSelectedJob] = useState<BackgroundJob | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);

  const { jobs, hasActiveJobs } = useClientJobs(clientId);

  if (!clientId) {
    return null;
  }

  const handleJobClick = (job: BackgroundJob) => {
    setSelectedJob(job);
    setShowDetailsDialog(true);
    overlayRef.current?.hide();
  };

  const handleDialogHide = () => {
    setShowDetailsDialog(false);
    setSelectedJob(null);
  };

  return (
    <>
      <Button
        className="job-status p-overlay-badge"
        onClick={(e) => overlayRef.current?.toggle(e)}
        tooltip={hasActiveJobs ? `${jobs.length} active job(s)` : 'No active jobs'}
        tooltipOptions={{ position: 'left' }}
        text
      >
        <span className={`ms-out ms-out-change-circle ${hasActiveJobs ? 'loading-spinner' : ''}`} />
        {hasActiveJobs && <Badge value={jobs.length} className="job-status-badge" severity="info" />}
      </Button>

      <OverlayPanel ref={overlayRef} className="user-menu-panel job-dropdown">
        <div className="user-menu-header">Active Jobs</div>
        {jobs.length === 0 ? (
          <p className="empty-message">No active jobs</p>
        ) : (
          jobs.map((job) => (
            <Button
              key={job.id}
              className="user-menu-item"
              link
              onClick={() => handleJobClick(job)}
            >
              <div className="job-item-content">
                <div className="job-item-header">
                  <span>{formatJobType(job.job_type)}</span>
                  <Tag value={job.status} severity={statusSeverityMap[job.status]} />
                </div>
                {job.status === 'running' && (
                  <ProgressBar value={getProgressPercent(job)} showValue={false} className="progress-mini" />
                )}
                {job.progress_message && (
                  <small className="status-text-muted">{job.progress_message}</small>
                )}
              </div>
            </Button>
          ))
        )}
      </OverlayPanel>

      <JobDetailsDialog
        job={selectedJob}
        visible={showDetailsDialog}
        onHide={handleDialogHide}
      />
    </>
  );
};

export default JobIndicator;
